import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    CircularProgress,
    Box,
    Autocomplete,
} from '@mui/material';
import { useSnackbar } from 'contexts/SnackbarContextProvider';

import { useTranslation } from "react-i18next";

const TransferOwnershipModal = ({
    open,
    onClose,
    workspaceId,
    loggedUserID,
    workspaceWorkers,
    sendConfirmationEmail,
    verifyConfirmationCode
}) => {
    const { showSnackbar } = useSnackbar();
    const [selectedUser, setSelectedUser] = useState(null);
    const [isLocked, setIsLocked] = useState(false);
    const [countdown, setCountdown] = useState(10);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [isCodeValid, setIsCodeValid] = useState(false);
    const { t } = useTranslation();

    const filteredWorkspaceWorkers = workspaceWorkers.filter(worker => worker.id !== loggedUserID);


    useEffect(() => {
        let timer;
        if (isLocked && countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        if (countdown === 0) {
            setIsLocked(false);
        }
        return () => clearTimeout(timer);
    }, [isLocked, countdown]);

    const handleUserSelect = (event, user) => {
        setSelectedUser(user);
        if (user) {
            setIsLocked(true);
            setCountdown(10);
            setEmailSent(false);
            setConfirmationCode('');
            setIsCodeValid(false);
        }
    };

    const handleSendEmail = async () => {
        if (!selectedUser) return;
    
        try {
            await sendConfirmationEmail(workspaceId, selectedUser.id);
            setEmailSent(true);
            showSnackbar(t('transferOwnership.snackbar.emailSentSuccess'), 'success');
        } catch (error) {
            console.error('Failed to send confirmation email:', error.message);
            showSnackbar(t('transferOwnership.snackbar.emailSentError'), 'error');
        }
    };
    
    const handleConfirm = async () => {
        if (!isCodeValid) return;
    
        setIsSubmitting(true);
    
        try {
            await verifyConfirmationCode(workspaceId, confirmationCode);
            showSnackbar(t('transferOwnership.snackbar.codeVerifiedSuccess'), 'success');
            onClose();
        } catch (error) {
            console.error('Failed to verify confirmation code:', error.message);
            showSnackbar(t('transferOwnership.snackbar.codeVerifiedError'), 'error');
        } finally {
            setIsSubmitting(false);
        }
    };
    

    const validateConfirmationCode = (code) => {
        const isValid = /^\d{6}$/.test(code);
        setIsCodeValid(isValid);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle style={{ backgroundColor: '#1F1F1F' }}>
                {t("transferOwnership.title")}
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: '#2D2D2D' }}>
                <Typography variant="body1" sx={{ marginTop: '12px' }}>
                    {t("transferOwnership.warning")}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '24px', marginBottom: '12px' }}>
                    {t("transferOwnership.selectUserPrompt")}
                </Typography>
                <Autocomplete
                    options={filteredWorkspaceWorkers}
                    getOptionLabel={(option) => option.full_name || ''}
                    renderOption={(props, option) => (
                        <Box {...props} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body1">{option.full_name}</Typography>
                            <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                                {option.email}
                            </Typography>
                        </Box>
                    )}
                    onChange={handleUserSelect}
                    value={selectedUser}
                    fullWidth
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("transferOwnership.selectOrSearch")}
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                className: 'dropdown-select-input-prop',
                            }}
                        />
                    )}
                    classes={{
                        paper: 'dropdown-select-input-prop',
                        listbox: 'dropdown-select-input-prop-listbox',
                        option: 'dropdown-select-input-prop-option',
                    }}
                />
                {isLocked && (
                    <Box mt={2}>
                        <Typography variant="body2">
                            {t("transferOwnership.confirmationPrompt")} {selectedUser?.full_name }.
                        </Typography>
                        <Typography>{t("transferOwnership.irreversibleAction")}</Typography>
                        <Typography variant="h4" sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            {countdown}s
                        </Typography>
                    </Box>
                )}
                {emailSent && countdown === 0 && (
                    <Box mt={2}>
                        <Typography variant="body2">
                            {t("transferOwnership.enterConfirmationCode")}
                        </Typography>
                        <TextField
                            variant="outlined"
                            fullWidth
                            value={confirmationCode}
                            onChange={(e) => {
                                const value = e.target.value.slice(0, 6);
                                setConfirmationCode(value);
                                validateConfirmationCode(value);
                            }}
                            inputProps={{
                                maxLength: 6,
                                style: { color: '#FFFFFF' },
                            }}
                            style={{
                                backgroundColor: '#2D2D2D',
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions style={{ backgroundColor: '#2D2D2D' }}>
                <Button
                    onClick={onClose}
                    color="secondary"
                    disabled={isSubmitting}
                    style={{ color: '#FFFFFF' }}
                    className="decline-button"
                >
                    {t("global.cancel")}
                </Button>
                {!emailSent && (
                    <Button
                        onClick={handleSendEmail}
                        color="primary"
                        disabled={!selectedUser || countdown > 0}
                        style={{ color: '#FFFFFF' }}
                        className="accept-button"
                    >
                        {t("transferOwnership.sendEmail")}
                    </Button>
                )}
                {emailSent && (
                    <Button
                        onClick={handleConfirm}
                        color="primary"
                        disabled={!isCodeValid || isSubmitting}
                        className="accept-button"
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : t("transferOwnership.confirmTransfer")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TransferOwnershipModal;
